import React, { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContactsIcon from "@mui/icons-material/Contacts";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BuildIcon from "@mui/icons-material/Build";
import SendIcon from "@mui/icons-material/Send";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import LoginIcon from "@mui/icons-material/Login";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { ListItem, ListItemButton, SvgIcon, Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NotFound } from "./components/NotFound";
import { GrinchIconNoColor } from "./components/icons/GrinchIcon";
import { FadeIn } from "../components/motion";
import {ADMIN, PEOPLE_PARTNER, REINDEER, SENDER} from "./ListItems";

// Lazy load all view components
const Dashboard = lazy(() => import("./screens/Dashboard"));
const HistoryView = lazy(() => import("./screens/HistoryView"));
const ReceiverView = lazy(() => import("./screens/ReceiverView"));
const SenderView = lazy(() => import("./screens/SenderView"));
const UserView = lazy(() => import("./screens/UserView"));
const UsersView = lazy(() => import("./screens/UsersView"));
const AdminView = lazy(() => import("./screens/AdminView"));
const SecretSantasView = lazy(() => import("./screens/SecretSantasView"));
const CouriersView = lazy(() => import("./screens/couriers/CouriersView"));
const UsersLoginView = lazy(() => import("./screens/UsersLoginView"));
const SecretSanta = lazy(() => import("./screens/component/secretSanta/SecretSanta"));
const GrinchesView = lazy(() => import("./screens/GrinchesView"));
const ProjectAssignmentsView = lazy(() => import("./screens/ProjectAssignmentsView"));

// Types
export interface User {
  id: string | number;
  name: string;
  roles: string[];
  [key: string]: any;
}

export interface MenuItemData {
  name: string;
  hName: string;
  icon: React.ReactElement;
  roles: string[];
  color?: string; // Module-specific color
}

interface ContainerItemProps {
  current?: MenuItemData;
  user: any;
  setShowSearch: (show: boolean) => void;
  searchValue: string | null;
  onFetchFailed: (error: any) => void;
  snackBarAddMessage: (message: string, variant: any) => void;
  courieId?: string | number;
  santaState: number;
  [key: string]: any; // Flexible props for compatibility with various screens
}

interface MenuItemProps {
  data: MenuItemData;
  open: boolean;
  onClick: (data: MenuItemData) => void;
  currentMenu?: MenuItemData;
}

interface MainListItemsProps {
  open: boolean;
  onClick: (data: MenuItemData) => void;
  currentMenu?: MenuItemData;
  user: any;
  santaState: number;
}

// Santa Claus Icon Component
const SantaClausIcon = () => (
  <SvgIcon viewBox="0 0 512 512">
    <g id="SVGRepo_bgCarrier" strokeWidth="0" />
    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
    <g id="SVGRepo_iconCarrier">
      <g>
        <path
          fill={"#ff0000"}
          d="M464.128,298.59c-5.931-19.358-20.476-64.208-38.389-111.549c-10.415-27.539-21.928-55.788-33.558-80.234 c-11.718-24.464-23.152-44.974-35.268-58.208c-14.271-15.432-29.853-27.947-49.07-36.27C288.627,3.989,266.291,0,238.822,0 c-26.352,0.009-56.7,0.381-83.956,4.228c-13.641,1.942-26.547,4.734-38.043,9.086c-11.434,4.378-21.84,10.354-29.383,19.572 l-0.009,0.017C76.183,46.818,63.605,69.172,53.218,93.6c-9.413,22.425-16.823,46.064-18.108,66.938 c-11.788,8.926-19.482,23.02-19.492,38.965c0.009,16.965,8.82,31.696,21.92,40.409c-2.836,11.976-7.977,32.397-16.46,62.134 c-2.26,7.863-3.333,15.6-3.333,23.107c0.018,25.333,12.082,46.579,27.238,63.518c15.246,17.018,34.028,30.544,50.408,41.491 c18.028,12.019,44.38,31.014,66.07,46.915c10.858,7.942,20.59,15.14,27.566,20.334c3.51,2.606,6.329,4.697,8.269,6.151 c0.967,0.718,1.711,1.285,2.225,1.666l0.576,0.426h0.009l0.062,0.052l0.071,0.063l0.027,0.017l0.009-0.009l5.335,4.016l2.944,2.207 l8.278-6.223l0.097-0.071l0.026-0.026c0.399-0.284,4.521-3.377,11.107-8.261c20.962-15.582,66.557-49.193,93.653-67.248 c16.371-10.938,35.144-24.482,50.39-41.483c9.254-10.344,17.31-22.301,22.213-35.791c3.447,25.04,24.694,44.398,50.682,44.398 c28.382,0,51.374-22.992,51.383-51.374C496.364,324.348,482.945,306.186,464.128,298.59z M64.546,173.152H349.28 c14.546,0.026,26.308,11.798,26.325,26.352c-0.017,14.51-11.779,26.29-26.325,26.307H64.546 c-14.555-0.017-26.317-11.797-26.343-26.307C38.229,184.949,49.991,173.178,64.546,173.152z M310.494,281.297 c0.009,11.665-0.958,24.322-3.687,35.144c-2.695,10.884-7.198,19.58-13.039,24.278c-3.785,3.014-6.036,3.536-8.411,3.599 c-2.198,0.017-4.982-0.753-8.563-2.482c-5.336-2.535-11.948-7.09-19.979-11.212c-8.03-4.112-17.807-7.774-29.426-7.756 c-5.877-0.018-12.206,1.001-18.835,3.128c-6.63-2.118-12.95-3.128-18.854-3.128c-15.528,0.009-27.583,6.435-36.979,11.93 c-4.715,2.766-8.864,5.336-12.418,7.038c-3.571,1.729-6.363,2.482-8.562,2.482c-2.375-0.062-4.627-0.603-8.412-3.608 c-5.841-4.689-10.335-13.384-13.029-24.268c-2.739-10.822-3.705-23.48-3.697-35.144c-0.008-15.83,1.756-29.924,2.668-35.374 l0.398-2.438h197.757l0.407,2.429C308.738,251.373,310.503,265.467,310.494,281.297z M355.273,373.63 c-12.986,14.563-30.296,27.238-46.082,37.75c-31.103,20.742-83.984,59.99-100.638,72.408 c-16.655-12.418-69.544-51.666-100.647-72.408c-15.777-10.512-33.097-23.196-46.073-37.75 c-13.047-14.634-21.503-30.632-21.494-48.476c0-5.37,0.744-10.964,2.437-16.903c9.094-31.794,14.412-53.226,17.222-65.227 c1.516,0.16,2.996,0.461,4.547,0.461H93.84c-1.516,9.582-4.901,27.318-4.91,37.812c0.009,12.613,0.984,26.502,4.228,39.443 c3.262,12.888,8.749,25.182,18.977,33.647c6.054,5.026,12.976,7.658,19.607,7.596c6.089-0.009,11.434-1.923,16.23-4.228 c7.188-3.492,13.614-7.986,20.35-11.389c6.754-3.431,13.491-5.815,21.379-5.832c4.654,0.009,9.804,0.833,15.795,3.032l3.059,1.134 l3.067-1.134c5.982-2.208,11.123-3.032,15.768-3.032c10.485,0,19.128,4.281,28.062,9.51c4.449,2.598,8.881,5.389,13.685,7.72 c4.795,2.296,10.131,4.211,16.221,4.228c6.638,0.054,13.561-2.57,19.598-7.604c10.237-8.465,15.724-20.759,18.994-33.647 c3.245-12.949,4.22-26.83,4.22-39.443c0-11.505-2.952-29.428-4.069-37.812h25.181c2.65,0,5.221-0.336,7.748-0.78 c2.774,11.93,8.128,33.478,17.292,65.546c1.684,5.939,2.447,11.532,2.447,16.903C376.776,343.005,368.32,358.996,355.273,373.63z M444.999,374.72c-15.901-0.036-28.762-12.888-28.788-28.798c0.026-15.91,12.887-28.762,28.788-28.798 c15.911,0.035,28.763,12.888,28.798,28.798C473.762,361.832,460.91,374.684,444.999,374.72z"
        ></path>
      </g>
    </g>
  </SvgIcon>
);

// Loading fallback component with FadeIn animation
const LoadingFallback = () => {
  const theme = useTheme();
  return (
    <FadeIn delay={0}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: theme.palette.primary.main,
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Box
            sx={{
              fontFamily: '"Instrument Sans", sans-serif',
              fontSize: "1rem",
              color: theme.palette.text.secondary,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            Loading...
          </Box>
        </motion.div>
      </Box>
    </FadeIn>
  );
};

// Helper function to create menu item data
function createListItemData(
  name: string,
  hName: string,
  icon: React.ReactElement,
  roles: string[],
  color?: string
): MenuItemData {
  return { name, hName, icon, roles, color };
}

// Menu data with module-specific colors
export const menuData: MenuItemData[] = [
  createListItemData("secretSanta", "Secret Santa", <SantaClausIcon />, [], "santa"),
  createListItemData("send", "Send", <SendIcon />, [ADMIN, SENDER], "email"),
  createListItemData("couriers", "Couriers", <DeliveryDiningIcon />, [], "courier"),
  createListItemData("history", "History", <AccessTimeIcon />, [ADMIN, SENDER]),
  createListItemData("receivers", "Receivers", <ContactsIcon />, [ADMIN]),
  createListItemData("users", "Users", <PeopleIcon />, [ADMIN]),
  createListItemData("usersLogins", "Users Logins", <LoginIcon />, [ADMIN]),
  createListItemData("secretSantas", "Secret Santas", <AcUnitIcon />, [ADMIN, REINDEER]),
  createListItemData("grinches", "Grinches", <GrinchIconNoColor />, [ADMIN]),
  createListItemData("dashboard", "Dashboard", <DashboardIcon />, [ADMIN, SENDER]),
  createListItemData("projectAssignments", "Project Assignments", <AssignmentIcon />, [ADMIN, PEOPLE_PARTNER]),
  createListItemData("admin", "Admin", <BuildIcon />, [ADMIN, REINDEER]),
  createListItemData("user", "User", <PersonIcon />, [], "user"),
];

// Container component that renders the appropriate screen with lazy loading
export function ContainerItem(props: ContainerItemProps) {
  // Cast courieId to number for CouriersView
  const couriersProps = {
    ...props,
    courieId: props.courieId ? (typeof props.courieId === "string" ? parseInt(props.courieId, 10) : props.courieId) : undefined,
  };

  // Map of component names to lazy-loaded components
  const componentMap: Record<string, React.ReactNode> = {
    dashboard: <Dashboard {...props} />,
    send: <SenderView {...props} />,
    history: <HistoryView {...props} />,
    receivers: <ReceiverView {...props} />,
    users: <UsersView {...props} />,
    usersLogins: <UsersLoginView {...props} />,
    user: <UserView {...props} />,
    couriers: <CouriersView {...couriersProps} />,
    secretSantas: <SecretSantasView {...props} />,
    grinches: <GrinchesView {...props} />,
    secretSanta: <SecretSanta receiverId={props.user.id} {...props} />,
    admin: <AdminView {...props} />,
    projectAssignments: <ProjectAssignmentsView {...props} />,
    notFound: <NotFound />,
  };

  const currentComponent = componentMap[props.current?.name || "notFound"];

  return (
    <Suspense fallback={<LoadingFallback />}>
      {currentComponent}
    </Suspense>
  );
}

// Menu item component with animations and module colors (memoized for performance)
const MenuItem = React.memo(({ data, open, onClick, currentMenu }: MenuItemProps) => {
  const theme = useTheme();
  const isSelected = currentMenu?.name === data.name;

  // Get module color or default
  const getModuleColor = (colorKey?: string) => {
    if (!colorKey) return theme.palette.primary.main;
    switch (colorKey) {
      case "courier":
        return theme.palette.courier?.main || "#F59E0B";
      case "santa":
        return theme.palette.santa?.main || "#6B9080";
      case "email":
        return theme.palette.email?.main || "#3B82F6";
      case "user":
        return theme.palette.user?.main || "#10B981";
      default:
        return theme.palette.primary.main;
    }
  };

  const moduleColor = getModuleColor(data.color);

  return (
    <ListItem key={data.name} disablePadding sx={{ display: "block" }}>
      <ListItemButton
        component={motion.div}
        whileHover={{
          backgroundColor: theme.palette.action.hover,
          x: 4,
        }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2 }}
        sx={{
          minHeight: 48,
          justifyContent: open ? "initial" : "center",
          px: 2.5,
          position: "relative",
          ...(isSelected && {
            backgroundColor: theme.palette.action.selected,
          }),
        }}
        onClick={() => onClick(data)}
      >
        {/* Animated color accent bar */}
        {isSelected && (
          <motion.div
            layoutId="activeIndicator"
            style={{
              position: "absolute",
              left: 0,
              top: 6,
              bottom: 6,
              width: 4,
              backgroundColor: moduleColor,
              borderRadius: "0 4px 4px 0",
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          />
        )}
        <Box
          component={motion.div}
          whileHover={
            data.color
              ? {
                  color: moduleColor,
                  scale: 1.1,
                }
              : {}
          }
          sx={{
            minWidth: 0,
            mr: open ? 3 : "auto",
            justifyContent: "center",
            color: isSelected ? moduleColor : "inherit",
            transition: "color 0.2s ease",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              color: "inherit",
            }}
          >
            {data.icon}
          </ListItemIcon>
        </Box>
        <ListItemText
          primary={data.hName}
          sx={{
            opacity: open ? 1 : 0,
            transition: "opacity 0.2s ease",
          }}
          primaryTypographyProps={{
            sx: {
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? moduleColor : "inherit",
            },
          }}
        />

        {/* Module badge (optional) */}
        {data.color && open && (
          <Box
            component={motion.div}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: moduleColor,
              opacity: isSelected ? 1 : 0.4,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to avoid unnecessary re-renders
  return (
    prevProps.open === nextProps.open &&
    prevProps.data.name === nextProps.data.name &&
    prevProps.currentMenu?.name === nextProps.currentMenu?.name
  );
});

MenuItem.displayName = "MenuItem";

// Main list items component
export default function MainListItems({ open, onClick, currentMenu, user, santaState }: MainListItemsProps) {
  const menu = menuData
    .filter((item) => item.roles.length === 0 || item.roles.some((el) => user.roles.includes(el)))
    .filter((item) => item.name !== "secretSanta" || santaState !== 0)
    .map((item, index) => (
      <motion.div
        key={item.name}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{
          delay: index * 0.05,
          duration: 0.3,
        }}
      >
        <MenuItem open={open} data={item} onClick={onClick} currentMenu={currentMenu} />
      </motion.div>
    ));

  return <div>{menu}</div>;
}
